/**
 * One graded question's feedback (plan docs/plans/lms-plan.md 7.2, 6.2 rule
 * 5, R21, 18.15; WP-40): every option shown, which one the learner chose,
 * which were correct, each option's feedback sentence (right or wrong, it
 * says why) and the question's explanation. Used question by question for
 * feedback_mode after_each and all together on the results for at_end; the
 * feedback comes from the submit answer (18.15), never from the draw.
 *
 * Every state is a word as well as a color (section 13).
 */
import { CheckCircle2, XCircle } from 'lucide-react';
import type { AttemptQuestion, QuestionResult } from '../../api/attempts';
import { microLabelCls, optionCls, pillCls } from './styles';

export interface QuestionFeedbackProps {
  question: AttemptQuestion;
  result: QuestionResult | undefined;
  number: number;
  total: number;
}

export function QuestionFeedback({ question, result, number, total }: QuestionFeedbackProps) {
  const options = Array.isArray(question.options) ? question.options : [];
  const selected = result?.selected ?? [];
  const correctOptions = result?.correct_options ?? [];
  const feedback = result?.feedback ?? {};
  const correct = result?.correct === true;

  return (
    <div className="space-y-3" data-testid="lms-question-feedback" data-question-id={question.id}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={microLabelCls}>{`Question ${number} of ${total}`}</span>
        {result ? (
          <span className={pillCls(correct ? 'green' : 'red')} data-testid="lms-question-verdict">
            {correct ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {correct ? 'Correct' : 'Incorrect'}
          </span>
        ) : null}
      </div>
      <p className="text-base font-medium text-gray-900 dark:text-gray-100">{question.stem}</p>
      {!result ? (
        <p className="text-sm text-red-600 dark:text-red-400">No feedback came back for this question.</p>
      ) : (
        <ul className="space-y-2">
          {options.map((option) => {
            const isSelected = selected.includes(option.id);
            const isCorrect = correctOptions.includes(option.id);
            const state = isCorrect ? 'correct' : isSelected ? 'incorrect' : 'muted';
            const text = feedback[option.id];
            return (
              <li key={option.id} className={optionCls(state)} data-testid="lms-feedback-option" data-option-id={option.id}>
                <span className="min-w-0 flex-1">
                  <span className="block">{option.text}</span>
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    {isCorrect && <span className={pillCls('green')}>Correct answer</span>}
                    {isSelected && <span className={pillCls(isCorrect ? 'green' : 'red')}>Your answer</span>}
                  </span>
                  {text ? (
                    <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300" data-testid="lms-option-feedback">
                      {text}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {result?.explanation ? (
        <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-question-explanation">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Explanation: </span>
          {result.explanation}
        </p>
      ) : null}
    </div>
  );
}

export default QuestionFeedback;
